import "./style.css";
import "@visiblethread/react-components/react-components.css";
import "@visiblethread/visi/visi.css";
import "scss/app.scss";
import "utils/chartSetup";

import { GlobalAlertHolder } from "components/Alert";
import ErrorFallbackModal from "components/Modal/ErrorFallbackModal";
import SessionResolver from "components/SessionResolver";
import SpinnerWithLoadMask from "components/SpinnerWithLoadMask";
import SessionProvider from "layout/add-in/SessionProvider";
import ModalsHolder from "layout/discovery/ModalsHolder";
import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { HashRouter, MemoryRouter } from "react-router-dom";
import { store } from "slices/store";
import { bootstrapOfficeAddIn } from "utils/add-in";
import { initSentryTracking } from "utils/sentry";

import NavigationBridge from "./NavigationBridge";

const container = document.getElementById("root");
const root = createRoot(container);

const [isAddIn, errorMessage] = await bootstrapOfficeAddIn();

if (isAddIn) {
  document.body.classList.add("add-in-mode");
}

const { default: AppComponent } = isAddIn
  ? await import("./AddInApp")
  : await import("./BrowserApp");
const AppRouter = isAddIn ? MemoryRouter : HashRouter;
const SessionWrapper = isAddIn ? SessionProvider : SessionResolver;

if (!isAddIn) {
  initSentryTracking();
}

// Dropped before rendering rather than after, so the add-in error path below cannot
// leave it spinning forever. The initial render is synchronous and SessionResolver's
// first paint is the library Spinner, so there is no gap to see.
document.getElementById("vt-boot-spinner")?.remove();

if (errorMessage) {
  throw new Error(errorMessage);
}

root.render(
  <Provider store={store}>
    {/* v7_startTransition makes a route change a React transition, which keeps the page
        you are leaving on screen while the next route's chunk loads instead of blanking
        it out to the Suspense fallback. It does nothing on a reload — there is no
        previous page to hold — so the fallback still covers that case. */}
    <AppRouter future={{ v7_startTransition: true }}>
      <NavigationBridge />

      <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
        <SessionWrapper>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <ModalsHolder />
          <GlobalAlertHolder />

          <AppComponent />
        </SessionWrapper>
      </ErrorBoundary>
    </AppRouter>
  </Provider>
);
